// frontend/src/hooks/useEntitlements.js
//
// Loads the caller's tenant's resolved feature entitlements once and exposes
// { entitlements, loading, isEnabled }. Backed by GET /api/v1/tenant/entitlements
// (worker/lib/entitlements.js). UX only — every API route still enforces its
// own gate server-side; this just hides modules the tenant isn't entitled
// to so a nav item doesn't lead to a 403.
//
// Cached at module scope for the session so multiple consumers
// (Dashboard nav + individual pages) share one fetch. logout() elsewhere
// clears localStorage; a full reload clears this.
import { useEffect, useState } from 'react';
import { apiFetch } from '../api/client';

let _cache = null; // { entitlements } once loaded
let _inflight = null;

async function loadOnce() {
  if (_cache) return _cache;
  if (_inflight) return _inflight;
  _inflight = (async () => {
    try {
      const res = await apiFetch('/tenant/entitlements');
      const data = await res.json().catch(() => ({}));
      _cache = data.success ? { entitlements: data.entitlements || {} } : { entitlements: {} };
    } catch {
      _cache = { entitlements: {} };
    } finally {
      _inflight = null;
    }
    return _cache;
  })();
  return _inflight;
}

export function clearEntitlementsCache() {
  _cache = null;
  _inflight = null;
}

export function useEntitlements() {
  const [state, setState] = useState(_cache || { entitlements: null });
  const [loading, setLoading] = useState(!_cache);

  useEffect(() => {
    let alive = true;
    if (_cache) {
      setState(_cache);
      setLoading(false);
      return undefined;
    }
    loadOnce().then((c) => {
      if (alive) {
        setState(c);
        setLoading(false);
      }
    });
    return () => { alive = false; };
  }, []);

  const isEnabled = (feature) => {
    const e = state.entitlements;
    // While loading (null), default to VISIBLE — a brief flash of a nav item
    // that then disappears is better than hiding a module the tenant has.
    if (!e) return true;
    return e[feature] ? !!e[feature].enabled : true;
  };

  return { entitlements: state.entitlements, loading, isEnabled };
}
